// The Screens mode (`/c/{slug}/screens`; SCREENS-S1, decision 2026-10-28 §4 and §7.5;
// SCREENS-VIEWER): a two-pane workspace. Left, the sessions (Needs you · Live · Waiting); right, the
// selected one live, filling the space (`SessionPane` → `LiveScreen`). The selection is in the URL
// (`?lease=`); one connection at a time — selecting another row swaps it. Agents start sessions;
// people join through Needs you or Watch, log in to their accounts from the Accounts tab (Log in →
// a login browser, selected in control, Done closes it), and admins may open a bare desktop or
// browser. No computer: an honest empty state, never sample rows.
import { Badge, EmptyState, InfoTip, LocationHeader, toast, ViewTabs } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MonitorIcon, MousePointerClickIcon } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { type CompanyAccess, isManager } from "../company";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { useSession } from "../use-feature";
import { AccountsPanel } from "./accounts-panel";
import {
  browserAccountsQuery,
  findLease,
  groupSessions,
  type Lease,
  type Stack,
  screensKeys,
  screensOverviewQuery,
} from "./screens-queries";
import { type OpenKind, openBareLease, openLoginLease } from "./session-actions";
import { type PendingOpen, SessionList } from "./session-list";
import { SessionPane } from "./session-pane";

type Tab = "screens" | "accounts";
const TABS: { value: Tab; label: string }[] = [
  { value: "screens", label: "Screens" },
  { value: "accounts", label: "Accounts" },
];

const fail = (e: unknown, what: string) =>
  toast.add({ title: problemText(e, what), type: "error" });

/** "Starting desktop… 12 s" while the computer starts it (desktops ~5–30 s, browsers ~30 s). */
export function startingStep(p: Pick<PendingOpen, "kind" | "since">, now: number): string {
  const s = Math.max(0, Math.floor((now - p.since) / 1000));
  return `Starting ${p.kind}…${s >= 2 ? ` ${s} s` : ""}`;
}

export function ScreensScreen({
  api,
  company,
  selected,
  onSelect,
  initialTab = "screens",
  login,
  onLoginTaken,
  kind,
  views,
  endedWhenMissing = false,
}: {
  api: Api;
  company: CompanyAccess;
  /** `?lease=`: the selected session. */
  selected: string | null;
  onSelect: (leaseId: string | null) => void;
  /** CONNECTIONS-AREA: `?tab=accounts` opens on Accounts. */
  initialTab?: Tab;
  /** CONNECTIONS-AREA: `?login=`: open this account's login browser once (Team › Connections). */
  login?: string;
  /** The login was started (or the account is not there): the page drops `?login=`. */
  onLoginTaken?: () => void;
  /** CANVAS-BLOCKS: only this kind's sessions (Live › Browser / Computer); unset: all. */
  kind?: Lease["kind"];
  /** CANVAS-BLOCKS: the Live page's view tabs, beside this screen's own. */
  views?: ReactNode;
  /**
   * CANVAS-BLOCKS: a selected session not in my overview (ended, or not mine to see) says
   * "This session has ended" instead of trying to connect. Checked once more against a fresh read
   * first (a session an agent just opened may not be in the last poll).
   */
  endedWhenMissing?: boolean;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const { me: session } = useSession();
  const me = session?.memberships.find((m) => m.company_id === co)?.membership_id ?? null;
  const admin = isManager(company);
  const overview = useQuery(screensOverviewQuery(api, co));
  const [tab, setTab] = useState<Tab>(initialTab);
  // `?tab=accounts` asked again while Live stays mounted (Team › Connections › Open in Live)
  useEffect(() => {
    if (initialTab === "accounts") setTab("accounts");
  }, [initialTab]);
  const loginAccounts = useQuery({ ...browserAccountsQuery(api, co), enabled: !!login });
  const [pending, setPending] = useState<PendingOpen[]>([]);
  const [pendingSel, setPendingSel] = useState<string | null>(null);
  const pendingSelRef = useRef<string | null>(null);
  pendingSelRef.current = pendingSel;
  const [now, setNow] = useState(() => Date.now());
  const refresh = () => qc.invalidateQueries({ queryKey: screensKeys.overview(co) });

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // The step text's clock, only while something is starting.
  useEffect(() => {
    if (!pending.length) return;
    const t = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(t);
  }, [pending.length]);

  const startOpen = (kind: OpenKind, label: string, run: () => Promise<Lease>) => {
    const key = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const p: PendingOpen = { key, kind, label, since: Date.now() };
    setNow(p.since);
    setPending((xs) => [...xs, p]);
    pendingSelRef.current = key;
    setPendingSel(key);
    setTab("screens");
    run().then(
      (l) => {
        qc.setQueryData(screensKeys.overview(co), (o: unknown) => withLease(o, l));
        void refresh();
        // Left the page meanwhile: the lease is in the list when I come back, no navigation.
        if (!alive.current) return;
        setPending((xs) => xs.filter((x) => x.key !== key));
        // Still looking at it: show the lease (waiting, or connecting).
        if (pendingSelRef.current === key) {
          pendingSelRef.current = null;
          setPendingSel(null);
          onSelect(l.id);
        }
      },
      (e) => {
        if (!alive.current) return;
        setPending((xs) => xs.filter((x) => x.key !== key));
        if (pendingSelRef.current === key) setPendingSel(null);
        fail(e, `Could not open a ${kind}.`);
      },
    );
  };

  // CONNECTIONS-AREA: `?login=` from Team › Connections › Accounts: start that login once.
  const loginTaken = useRef<string | null>(null);
  const loginRows = loginAccounts.data?.data;
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once per asked login
  useEffect(() => {
    if (!login) {
      // Taken and dropped from the URL: a later ask for the same account starts again
      loginTaken.current = null;
      return;
    }
    if (loginTaken.current === login || (!loginRows && !loginAccounts.isError)) return;
    loginTaken.current = login;
    const a = loginRows?.find((x) => x.id === login && x.can_manage);
    if (a)
      startOpen(a.kind === "desktop" ? "desktop" : "browser", a.label, () =>
        openLoginLease(api, co, a.id),
      );
    else setTab("accounts");
    onLoginTaken?.();
  }, [login, loginRows, loginAccounts.isError]);

  const bump = useMutation({
    mutationFn: (l: Lease) => api.client.screens.bump({ params: { co, id: l.id } }),
    onSuccess: refresh,
    onError: (e) => fail(e, "Could not move it up."),
  });
  // What was selected when Help was clicked: a late answer does not override a newer choice.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const help = useMutation({
    mutationFn: (v: { lease: Lease; from: string | null }) =>
      api.client.screens.takeOver({ params: { co, id: v.lease.id } }),
    onSuccess: (l, v) => {
      qc.setQueryData(screensKeys.overview(co), (o: unknown) => withLease(o, l));
      void refresh();
      if (alive.current && selectedRef.current === v.from) select(l.id);
    },
    onError: (e) => fail(e, "Could not take it over."),
  });

  const select = (id: string | null) => {
    setPendingSel(null);
    onSelect(id);
  };

  const data = overview.data;
  const groups = useMemo(() => {
    if (!data) return null;
    const g = groupSessions(data);
    if (!kind) return g;
    const mine = (l: Lease) => l.kind === kind;
    return {
      needsYou: g.needsYou.filter(mine),
      live: g.live.filter(mine),
      waiting: g.waiting.filter(mine),
    };
  }, [data, kind]);
  const operatorRows = data ? data.operator.filter((o) => !kind || o.kind === kind) : [];
  const lease = data && selected ? findLease(data, selected) : null;
  const pend = pendingSel ? (pending.find((p) => p.key === pendingSel) ?? null) : null;
  // The selected session is not in the overview: re-read once, then say it has ended.
  const missing = endedWhenMissing && !!selected && !!data && !lease && !pend;
  const [checked, setChecked] = useState<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per missing session
  useEffect(() => {
    if (!missing || !selected || checked === selected) return;
    let on = true;
    void overview.refetch().finally(() => {
      if (on) setChecked(selected);
    });
    return () => {
      on = false;
    };
  }, [missing, selected]);
  const ended = missing && checked === selected;
  const anySessions = groups
    ? groups.needsYou.length + groups.live.length + groups.waiting.length > 0
    : false;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-slot="screens-screen">
      <LocationHeader
        icon={MonitorIcon}
        title="Live"
        badge={
          data?.stack ? (
            <StackChip stack={data.stack} reachable={data.stack_reachable && !overview.isError} />
          ) : null
        }
        actions={
          <>
            {views}
            <ViewTabs
              aria-label="Screens"
              tabs={TABS}
              value={tab}
              onValueChange={(v) => setTab(v === "accounts" ? "accounts" : "screens")}
            />
          </>
        }
      />
      {overview.isPending ? (
        <Loading label="Loading screens" />
      ) : overview.isError && !data ? (
        // A failed refresh keeps the workspace (and its live connection); the chip says it.
        <LoadError what="screens" error={overview.error} />
      ) : tab === "accounts" ? (
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4 max-md:px-4">
          <AccountsPanel
            api={api}
            co={co}
            admin={admin}
            loggingIn={pending.length > 0}
            onLogIn={(a) =>
              startOpen(a.kind === "desktop" ? "desktop" : "browser", a.label, () =>
                openLoginLease(api, co, a.id),
              )
            }
          />
        </div>
      ) : !data?.stack || !groups ? (
        <div className="px-6 py-4 max-md:px-4">
          <EmptyState
            icon={MonitorIcon}
            title="No screens yet"
            description={
              <span className="inline-flex items-center gap-1">
                Your company's computer isn't set up.
                <InfoTip label="About the computer">
                  Browsers and desktops your agents work in. It appears here once your operator
                  connects it.
                </InfoTip>
              </span>
            }
          />
        </div>
      ) : (
        <div className="flex min-h-0 min-w-0 flex-1 max-md:flex-col" data-slot="screens-workspace">
          <SessionList
            groups={groups}
            operator={operatorRows}
            pending={pending}
            selected={pendingSel ?? selected}
            me={me}
            admin={admin}
            canBump={data.can_bump}
            stack={data.stack}
            opening={pending.length > 0}
            onSelect={(l) => select(l.id)}
            onSelectPending={(k) => setPendingSel(k)}
            onHelp={(l) => help.mutate({ lease: l, from: selected })}
            onOpen={(kind) =>
              startOpen(kind, kind === "desktop" ? "Desktop" : "Browser", () =>
                openBareLease(api, co, kind),
              )
            }
            onBump={(l) => bump.mutate(l)}
          />
          {pend ? (
            <SessionPane
              key={pend.key}
              api={api}
              co={co}
              slug={company.slug}
              leaseId={pend.key}
              lease={null}
              me={me}
              starting={startingStep(pend, now)}
            />
          ) : ended ? (
            <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center p-6">
              {/* Ended, gone, or not mine to see: one message, so nothing about it leaks. */}
              <EmptyState
                variant="compact"
                icon={MonitorIcon}
                title="This session has ended"
                data-slot="session-ended"
                description="Pick another session, or ask the agent to open a new one."
              />
            </div>
          ) : missing ? (
            <Loading label="Opening the session" />
          ) : selected ? (
            <SessionPane
              api={api}
              co={co}
              slug={company.slug}
              leaseId={selected}
              lease={lease}
              me={me}
              onClosed={() => select(null)}
            />
          ) : (
            <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center p-6">
              {/* The operator's own screens are read-only rows, not something to pick. */}
              {(kind ? anySessions : data.live.length || data.waiting.length) ? (
                <EmptyState
                  variant="compact"
                  icon={MousePointerClickIcon}
                  title="Pick a session"
                  data-slot="pick-session"
                />
              ) : (
                <EmptyState
                  variant="compact"
                  icon={MonitorIcon}
                  title="Nothing running"
                  data-slot="nothing-running"
                  description={
                    <span className="inline-flex items-center gap-1">
                      Agents open screens when a task needs one.
                      <InfoTip label="About sessions">
                        You join when one needs you, or watch any you may see. Log in to your saved
                        accounts from the Accounts tab.
                      </InfoTip>
                    </span>
                  }
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** The overview with a just-opened or taken-over lease in it, before the refetch lands. */
function withLease(o: unknown, l: Lease): unknown {
  const ov = o as { live?: Lease[]; waiting?: Lease[]; needs_you?: Lease[] } | undefined;
  if (!ov?.live || !ov.waiting) return o;
  const drop = (xs: Lease[]) => xs.filter((x) => x.id !== l.id);
  const swap = (xs: Lease[]) => xs.map((x) => (x.id === l.id ? l : x));
  return l.state === "queued"
    ? { ...ov, live: drop(ov.live), waiting: [...drop(ov.waiting), l] }
    : {
        ...ov,
        live: [...drop(ov.live), l],
        waiting: drop(ov.waiting),
        needs_you: l.state === "needs_you" ? swap(ov.needs_you ?? []) : drop(ov.needs_you ?? []),
      };
}

/** "1 desktop", "2 desktops". */
const count = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

function StackChip({ stack, reachable }: { stack: Stack; reachable: boolean }) {
  const c = stack.capacity;
  const free = (max: number, used: number) => Math.max(0, max - used);
  const ok = stack.state === "live";
  return (
    <span className="inline-flex min-w-0 items-center gap-1" data-slot="stack-chip">
      <Badge variant={ok ? "success" : "warning"} data-dot="">
        {stack.name} · {ok ? "Live" : stack.state === "stale" ? "Not responding" : "Retired"}
      </Badge>
      {reachable ? null : (
        <span className="inline-flex items-center" data-slot="stack-unreachable">
          <Badge variant="warning">Not answering</Badge>
          <InfoTip label="About the stack not answering">
            The computer didn't answer just now. Screens may be out of date; new ones wait.
          </InfoTip>
        </span>
      )}
      <span className="truncate text-xs text-muted-foreground max-sm:hidden">
        {count(free(c.browsers_max, c.browsers_used), "browser")} ·{" "}
        {count(free(c.desktops_max, c.desktops_used), "desktop")} free
      </span>
      <InfoTip label="About the computer">
        Your company's computer: {c.browsers_max} browsers, {c.desktops_max} desktops. Browsers{" "}
        {stack.health.browsers}, desktops {stack.health.desktops}, live view {stack.health.live}.
      </InfoTip>
    </span>
  );
}
